// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { ClientOnboarderService } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/ClientOnboarder/module';
import { DenyList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/Deny';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import Loading from '../../../components/Loading';
import { useAddWalletsToDenyList } from '../../../hooks/mutations/registry/denyList/useAddWalletsToDenyList';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import TemplateContract from '../../../utils/TemplateContract';
import {
  ERR_ADD_WALLET_TO_DENY_LIST,
  ERR_ADD_WALLET_TO_DENY_LIST_NOT_FOUND,
  SUC_ADD_WALLET_TO_DENY_LIST,
} from '../../../utils/strings';
import { groupCredentialsByHolder, isMemberOfDenyList } from './utils';

interface AddWalletDialogProps {
  open: boolean;
  onClose: () => void;
  denyList: TemplateContract<DenyList>;
  clientOnboarderServiceCid: ContractId<ClientOnboarderService>;
}

// Intentionally similar to allowList/AddWalletDialog.tsx — not consolidated because
// the mutation signatures and props differ (allowList has mode/disableDialog). If we need to do this one more time we can refactor then, but for now the duplication is minimal and keeps the components decoupled.
export const AddWalletDialog: React.FC<AddWalletDialogProps> = ({
  open,
  onClose,
  denyList,
  clientOnboarderServiceCid,
}) => {
  const [partyInput, setPartyInput] = useState('');
  const credentials = useCredentials();
  const addWallets = useAddWalletsToDenyList();
  const { enqueueSnackbar } = useSnackbar();
  const trimmedParty = partyInput.trim();
  const isFormValid = trimmedParty !== '';

  const availableHolders = useMemo(() => {
    if (!credentials.data) return [];

    const existingMemberHolders = new Set<string>();
    credentials.data.forEach((cred) => {
      if (isMemberOfDenyList(cred, denyList.payload.name)) {
        existingMemberHolders.add(cred.payload.holder);
      }
    });

    const availableCredentials = credentials.data.filter(
      (cred) => !existingMemberHolders.has(cred.payload.holder),
    );

    return groupCredentialsByHolder(availableCredentials);
  }, [credentials.data, denyList.payload.name]);

  const handleSubmit = async () => {
    if (!isFormValid) return;

    const matchingHolder = availableHolders.find((h) => h.holder === trimmedParty);

    if (!matchingHolder) {
      enqueueSnackbar(ERR_ADD_WALLET_TO_DENY_LIST_NOT_FOUND, { variant: 'error' });
      return;
    }

    const credentialCids = matchingHolder.credentials.map((c) => c.contractId);

    try {
      await addWallets.mutateAsync({
        clientOnboarderServiceCid,
        denyListCid: denyList.contractId,
        credentialCids,
      });
      enqueueSnackbar(SUC_ADD_WALLET_TO_DENY_LIST, { variant: 'success' });
      setPartyInput('');
      onClose();
    } catch (submitError) {
      console.error(ERR_ADD_WALLET_TO_DENY_LIST, submitError);
      enqueueSnackbar(ERR_ADD_WALLET_TO_DENY_LIST, { variant: 'error' });
    }
  };

  const handleCancel = () => {
    setPartyInput('');
    onClose();
  };

  const isLoading = credentials.isLoading || !credentials.data || addWallets.isPending;

  return (
    <FormDialog
      open={open}
      title="Add Wallet to Deny List"
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitName="Add Wallet"
      isSubmitDisabled={!isFormValid || isLoading}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the ID of the party you wish to add to this deny list.
          </Typography>
          <TextField
            label="Party"
            value={partyInput}
            onChange={(e) => {
              setPartyInput(e.target.value);
            }}
            fullWidth
            placeholder="Enter party ID"
            InputLabelProps={{ shrink: true }}
            InputProps={{ notched: true }}
          />
        </Box>
      )}
    </FormDialog>
  );
};
